import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import GitHubIcon from '@material-ui/icons/GitHub';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import DescriptionIcon from '@material-ui/icons/Description';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Link } from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import { useAsync } from 'react-use';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  hero: {
    padding: theme.spacing(8, 6),
    background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)',
    color: '#FFFFFF',
  },
  heroName: {
    fontWeight: 700,
    fontSize: '2.6rem',
    color: '#FFFFFF',
    marginBottom: theme.spacing(1),
    lineHeight: 1.2,
  },
  heroSubtitle: {
    fontSize: '1.05rem',
    color: '#94A3B8',
    marginBottom: theme.spacing(2),
    letterSpacing: '0.02em',
  },
  heroDesc: {
    fontSize: '1rem',
    color: '#CBD5E1',
    marginBottom: theme.spacing(4),
    maxWidth: 560,
    lineHeight: 1.7,
  },
  heroButton: {
    marginRight: theme.spacing(2),
    borderRadius: 6,
    textTransform: 'none',
    fontWeight: 600,
  },
  linkedInButton: {
    marginRight: theme.spacing(2),
    borderRadius: 6,
    textTransform: 'none',
    fontWeight: 600,
    backgroundColor: '#0077B5',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#005f91',
    },
  },
  outlinedButton: {
    color: '#CBD5E1',
    borderColor: '#475569',
    borderRadius: 6,
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
      borderColor: '#94A3B8',
      background: 'rgba(255,255,255,0.05)',
    },
  },
  section: {
    padding: theme.spacing(6, 6),
  },
  sectionTitle: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(4),
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(14,165,233,0.12)',
      borderColor: theme.palette.primary.main,
    },
  },
  cardContent: {
    flexGrow: 1,
    paddingBottom: theme.spacing(1),
  },
  cardType: {
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.75),
  },
  cardName: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  cardDesc: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    lineHeight: 1.6,
    marginBottom: theme.spacing(2),
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  tagChip: {
    margin: theme.spacing(0.25),
    fontSize: '0.7rem',
    height: 20,
    backgroundColor: '#F1F5F9',
    color: '#475569',
    fontWeight: 500,
  },
  cardActions: {
    padding: theme.spacing(1, 2, 2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  viewLink: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  ghButton: {
    textTransform: 'none',
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.25, 1),
  },
  techChip: {
    margin: theme.spacing(0.5),
    backgroundColor: '#EFF6FF',
    color: '#0284C7',
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
}));

function getGitHubUrl(entity: Entity): string | undefined {
  return entity.metadata.links?.find(l => l.icon === 'github')?.url;
}

function getEntityPath(entity: Entity): string {
  const namespace = entity.metadata.namespace ?? 'default';
  return `/catalog/${namespace}/${entity.kind.toLowerCase()}/${entity.metadata.name}`;
}

export const HomePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);

  const { value: entities, loading } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      filter: { kind: 'Component' },
    });
    return response.items;
  }, []);

  const allTags = useMemo(() => {
    if (!entities) return [];
    const seen = new Set<string>();
    entities.forEach(e => (e.metadata.tags ?? []).forEach(t => seen.add(t)));
    return Array.from(seen).sort();
  }, [entities]);

  return (
    <Box>
      {/* Hero */}
      <Box className={classes.hero}>
        <Typography className={classes.heroName}>Eduardo Tourinho</Typography>
        <Typography className={classes.heroSubtitle}>
          Senior Software Engineer &nbsp;·&nbsp; 20+ yrs &nbsp;·&nbsp; Java &nbsp;·&nbsp; Spring Boot &nbsp;·&nbsp; Microservices &nbsp;·&nbsp; AWS &nbsp;·&nbsp; Berlin
        </Typography>
        <Typography className={classes.heroDesc}>
          20+ years building scalable backend systems — Java, Spring Boot microservices,
          event-driven architecture, and cloud infrastructure on AWS. Experienced migrating
          legacy systems to modern architectures with zero downtime. This portal is my
          engineering catalog: projects, API specs, and docs in one place.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<GitHubIcon />}
          href="https://github.com/eduardotourinho"
          target="_blank"
          rel="noopener noreferrer"
          className={classes.heroButton}
        >
          GitHub Profile
        </Button>
        <Button
          variant="contained"
          startIcon={<LinkedInIcon />}
          href="https://linkedin.com/in/edutourinho"
          target="_blank"
          rel="noopener noreferrer"
          className={classes.linkedInButton}
        >
          LinkedIn
        </Button>
        <Button
          variant="outlined"
          startIcon={<DescriptionIcon />}
          href="https://drive.google.com/file/d/1GM2stGGusW1twzPM7nMoffOWA9RbyzZl/view?usp=sharing"
          target="_blank"
          rel="noopener noreferrer"
          className={classes.outlinedButton}
        >
          View Resume
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/catalog')}
          className={classes.outlinedButton}
          style={{ marginLeft: 16 }}
        >
          Browse Catalog
        </Button>
      </Box>

      {/* Projects */}
      <Box className={classes.section}>
        <Typography variant="h5" className={classes.sectionTitle}>
          Projects
        </Typography>

        {loading && (
          <Box className={classes.loadingBox}>
            <CircularProgress color="primary" />
          </Box>
        )}

        <Grid container spacing={3}>
          {(entities ?? []).map(entity => {
            const ghUrl = getGitHubUrl(entity);
            const entityPath = getEntityPath(entity);
            const type = (entity.spec as { type?: string } | undefined)?.type;

            return (
              <Grid item xs={12} sm={6} md={4} key={entity.metadata.uid}>
                <Card className={classes.card}>
                  <CardContent className={classes.cardContent}>
                    {type && (
                      <Typography className={classes.cardType}>{type}</Typography>
                    )}
                    <Typography className={classes.cardName}>
                      {entity.metadata.name}
                    </Typography>
                    <Typography className={classes.cardDesc}>
                      {entity.metadata.description ?? ''}
                    </Typography>
                    <Box>
                      {(entity.metadata.tags ?? []).map(tag => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          className={classes.tagChip}
                        />
                      ))}
                    </Box>
                  </CardContent>
                  <CardActions className={classes.cardActions}>
                    <Link to={entityPath} className={classes.viewLink}>
                      View details →
                    </Link>
                    {ghUrl && (
                      <Button
                        size="small"
                        startIcon={<GitHubIcon fontSize="small" />}
                        href={ghUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.ghButton}
                      >
                        GitHub
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Tech Stack */}
      {allTags.length > 0 && (
        <>
          <Divider />
          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              Tech Stack
            </Typography>
            <Box>
              {allTags.map(tag => (
                <Chip key={tag} label={tag} className={classes.techChip} />
              ))}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
